import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { ChevronRight, ListChecks, X } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/modern/components/ui/alert-dialog";
import { cn } from "@/modern/utils";
import type { ExceptionInboxItem } from "../mappers/buildExceptionInbox";
import { getStatusColor } from "../status/displayStatusColors";

type BulkAction = "excuse" | "reviewed";

type ExceptionInboxPanelProps = {
  items: ExceptionInboxItem[];
  dateFilter: string | null;
  canManage: boolean;
  isActing: boolean;
  onClose: () => void;
  onOpenItem: (item: ExceptionInboxItem) => void;
  onBulkExcuse: (attendanceIds: number[]) => Promise<void>;
  onBulkMarkReviewed: (attendanceIds: number[]) => Promise<void>;
};

type DayGroup = {
  date: string;
  label: string;
  items: ExceptionInboxItem[];
};

function formatDayLabel(date: string): string {
  try {
    return format(parseISO(date), "EEE · MMM d");
  } catch {
    return date;
  }
}

function formatPunchLine(clockIn: string | null, clockOut: string | null): string | null {
  const fmt = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
      return iso;
    }
  };
  if (clockIn && clockOut) return `${fmt(clockIn)} – ${fmt(clockOut)}`;
  if (clockIn) return `${fmt(clockIn)} →`;
  if (clockOut) return `→ ${fmt(clockOut)}`;
  return null;
}

function groupByDay(items: ExceptionInboxItem[]): DayGroup[] {
  const map = new Map<string, ExceptionInboxItem[]>();
  for (const item of items) {
    const list = map.get(item.date) ?? [];
    list.push(item);
    map.set(item.date, list);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, dayItems]) => ({
      date,
      label: formatDayLabel(date),
      items: dayItems,
    }));
}

function QueueCheckbox({
  checked,
  disabled,
  onCheckedChange,
  "aria-label": ariaLabel,
}: {
  checked: boolean | "indeterminate";
  disabled?: boolean;
  onCheckedChange: () => void;
  "aria-label": string;
}) {
  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      aria-label={ariaLabel}
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 [&_svg]:h-3 [&_svg]:w-3"
    />
  );
}

export function ExceptionInboxPanel({
  items,
  dateFilter,
  canManage,
  isActing,
  onClose,
  onOpenItem,
  onBulkExcuse,
  onBulkMarkReviewed,
}: ExceptionInboxPanelProps) {
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [confirmAction, setConfirmAction] = useState<BulkAction | null>(null);

  const visibleItems = useMemo(
    () => (dateFilter ? items.filter((i) => i.date === dateFilter) : items),
    [items, dateFilter]
  );

  const actionableItems = useMemo(
    () => visibleItems.filter((i) => i.actionable),
    [visibleItems]
  );

  const groups = useMemo(() => groupByDay(visibleItems), [visibleItems]);
  const actionableIds = useMemo(
    () => actionableItems.map((i) => i.attendanceId),
    [actionableItems]
  );
  const actionableKey = actionableIds.join(",");

  // Default: everything open is selected (re-sync when the open id set changes).
  useEffect(() => {
    const ids = actionableKey
      ? actionableKey.split(",").map((id) => Number(id))
      : [];
    setSelected(new Set(ids));
  }, [actionableKey]);

  const selectedCount = selected.size;
  const openCount = actionableIds.length;
  const allSelected = openCount > 0 && actionableIds.every((id) => selected.has(id));
  const someSelected = selectedCount > 0 && !allSelected;
  const masterChecked: boolean | "indeterminate" = allSelected
    ? true
    : someSelected
      ? "indeterminate"
      : false;

  const selectAll = () => setSelected(new Set(actionableIds));
  const clearSelection = () => setSelected(new Set());

  const toggleMaster = () => {
    if (allSelected) clearSelection();
    else selectAll();
  };

  const selectDay = (dayItems: ExceptionInboxItem[]) => {
    const ids = dayItems.filter((i) => i.actionable).map((i) => i.attendanceId);
    if (!ids.length) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = ids.every((id) => next.has(id));
      if (allOn) {
        for (const id of ids) next.delete(id);
      } else {
        for (const id of ids) next.add(id);
      }
      return next;
    });
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setOne = (id: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const runBulk = async () => {
    if (!confirmAction || selectedCount === 0) return;
    const ids = Array.from(selected);
    if (confirmAction === "excuse") await onBulkExcuse(ids);
    else await onBulkMarkReviewed(ids);
    setConfirmAction(null);
    setSelected(new Set());
  };

  const title =
    openCount === 0
      ? "All clear"
      : dateFilter
        ? formatDayLabel(dateFilter)
        : "Clear queue";

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-background">
      <header className="relative shrink-0 border-b border-border px-3 pb-2.5 pt-2.5">
        <div className="flex items-center gap-2.5">
          {canManage && openCount > 0 ? (
            <QueueCheckbox
              checked={masterChecked}
              disabled={isActing}
              onCheckedChange={toggleMaster}
              aria-label={allSelected ? "Deselect all" : "Select all open exceptions"}
            />
          ) : (
            <ListChecks className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
              {openCount > 0 ? (
                <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
                  {selectedCount}/{openCount}
                </span>
              ) : null}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="size-7 shrink-0 rounded-full p-0"
            onClick={onClose}
            aria-label="Close queue"
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>

      <ScrollArea className="min-h-0 flex-1">
        {visibleItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-muted">
              <ListChecks className="size-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">
              {dateFilter ? "Nothing for this day" : "Queue empty"}
            </p>
            <p className="mt-1 max-w-[220px] text-xs text-muted-foreground">
              {dateFilter
                ? "Try another date on the grid, or open Clear queue from the toolbar."
                : "No open exceptions in this range."}
            </p>
          </div>
        ) : (
          <div className="space-y-3 px-2.5 pb-4 pt-2">
            {groups.map((group, groupIndex) => {
              const dayActionable = group.items.filter((i) => i.actionable);
              const daySelectedCount = dayActionable.filter((i) =>
                selected.has(i.attendanceId)
              ).length;
              const dayAllSelected =
                dayActionable.length > 0 && daySelectedCount === dayActionable.length;
              const daySomeSelected =
                daySelectedCount > 0 && daySelectedCount < dayActionable.length;
              const dayChecked: boolean | "indeterminate" = dayAllSelected
                ? true
                : daySomeSelected
                  ? "indeterminate"
                  : false;

              return (
                <section
                  key={group.date}
                  className="animate-in fade-in-0 slide-in-from-bottom-1 overflow-hidden rounded-xl border border-border/80 bg-card/50 duration-300 fill-mode-both"
                  style={{ animationDelay: `${groupIndex * 35}ms` }}
                >
                  <div className="sticky top-0 z-[1] flex items-center gap-2.5 border-b border-border/70 bg-muted/55 px-3 py-2 backdrop-blur-md">
                    {canManage && dayActionable.length > 0 ? (
                      <QueueCheckbox
                        checked={dayChecked}
                        disabled={isActing}
                        onCheckedChange={() => selectDay(group.items)}
                        aria-label={`Select all for ${group.label}`}
                      />
                    ) : (
                      <span className="size-4 shrink-0" aria-hidden />
                    )}
                    <button
                      type="button"
                      disabled={!canManage || dayActionable.length === 0 || isActing}
                      onClick={() => canManage && selectDay(group.items)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left disabled:cursor-default"
                    >
                      <span className="truncate text-xs font-semibold tracking-wide text-foreground">
                        {group.label}
                      </span>
                      <span
                        className={cn(
                          "shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                          dayActionable.length === 0
                            ? "bg-muted text-muted-foreground"
                            : dayAllSelected
                              ? "bg-primary/15 text-primary"
                              : "bg-background/80 text-muted-foreground"
                        )}
                      >
                        {dayActionable.length > 0
                          ? `${daySelectedCount}/${dayActionable.length}`
                          : "Cleared"}
                      </span>
                    </button>
                  </div>

                  <ul>
                    {group.items.map((item) => {
                      const color = getStatusColor(item.displayStatus);
                      const punch = formatPunchLine(item.clockInAt, item.clockOutAt);
                      const checked = selected.has(item.attendanceId);
                      const settled = !item.actionable;
                      const meta = [punch, item.role].filter(Boolean).join(" · ");

                      return (
                        <li
                          key={item.key}
                          className={cn(
                            "group/row relative border-b border-border/60 last:border-b-0",
                            checked && "bg-primary/[0.07]",
                            settled && "opacity-60"
                          )}
                        >
                          <span
                            className={cn(
                              "absolute inset-y-0 left-0 w-[3px]",
                              color.accent,
                              !checked && !settled && "opacity-70",
                              settled && "opacity-40"
                            )}
                            aria-hidden
                          />
                          <div className="flex items-stretch pl-2.5">
                            {canManage ? (
                              <div className="flex items-center py-2.5 pl-1.5 pr-1">
                                <QueueCheckbox
                                  checked={checked}
                                  disabled={settled || isActing}
                                  onCheckedChange={() => {
                                    if (settled) return;
                                    setOne(item.attendanceId, !checked);
                                  }}
                                  aria-label={`Select ${item.resourceName}`}
                                />
                              </div>
                            ) : null}
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center gap-2.5 py-2.5 pl-1.5 pr-1 text-left transition-colors hover:bg-muted/25"
                              onClick={() => {
                                if (canManage && item.actionable) toggleOne(item.attendanceId);
                                else onOpenItem(item);
                              }}
                            >
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-2">
                                  <span className="truncate text-sm font-medium text-foreground">
                                    {item.resourceName}
                                  </span>
                                  <span
                                    className={cn(
                                      "shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-none",
                                      color.bg,
                                      color.text
                                    )}
                                  >
                                    {item.displayStatusLabel}
                                  </span>
                                </span>
                                {meta ? (
                                  <span className="mt-1 block truncate text-[11px] text-muted-foreground">
                                    {meta}
                                  </span>
                                ) : (
                                  <span className="mt-1 block text-[11px] italic text-muted-foreground/80">
                                    No punches
                                  </span>
                                )}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="flex items-center px-2.5 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                              aria-label={`Open ${item.resourceName}`}
                              onClick={() => onOpenItem(item)}
                            >
                              <ChevronRight className="size-4 opacity-50 transition-opacity group-hover/row:opacity-90" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {canManage && openCount > 0 ? (
        <footer className="shrink-0 border-t border-border bg-background/95 px-3 py-3 backdrop-blur-sm">
          {selectedCount === 0 ? (
            <Button
              type="button"
              className="h-10 w-full text-sm"
              disabled={isActing}
              onClick={selectAll}
            >
              Select all {openCount}
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 tabular-nums"
                  disabled={isActing}
                  onClick={() => setConfirmAction("excuse")}
                >
                  Excuse · {selectedCount}
                </Button>
                <Button
                  type="button"
                  className="h-10 tabular-nums"
                  disabled={isActing}
                  onClick={() => setConfirmAction("reviewed")}
                >
                  Reviewed · {selectedCount}
                </Button>
              </div>
              {!allSelected ? (
                <button
                  type="button"
                  disabled={isActing}
                  onClick={clearSelection}
                  className="w-full text-center text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  Clear selection
                </button>
              ) : null}
            </div>
          )}
        </footer>
      ) : null}

      <AlertDialog
        open={confirmAction != null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "excuse"
                ? `Excuse ${selectedCount}?`
                : `Mark ${selectedCount} reviewed?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === "excuse"
                ? "Excuses the selected exceptions. You can still reopen them from this list."
                : "Marks the selected exceptions reviewed. Times are unchanged."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isActing}
              onClick={(e) => {
                e.preventDefault();
                void runBulk();
              }}
            >
              {confirmAction === "excuse" ? "Excuse" : "Mark reviewed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
